import React, { useMemo, useState } from 'react';
import { useStore, useNav } from '../store/StoreContext';
import CapacityStrip from '../components/CapacityStrip';
import { Button, StatusPill, listPanelCls, listRowCompactCls, PageHeader, pageWrapCls } from '../components/ui';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../hooks/useToast';
import { format, addDays, parseISO } from 'date-fns';
import { datesToConfirm, tentativeKeys, dateKeysOf, fmtMoveDate } from '../lib/moves';
import {
    getClientStage,
    STAGE_NEEDS_FOLLOWUP,
    STAGE_AWAITING_WT,
    STAGE_WT_REVIEW,
    STAGE_AWAITING_BOOKING,
    STAGE_NEAR_MOVE,
} from '../lib/leadPipeline';

// Dashboard — operational command center. Top of page is six lead/operational
// queues built from getClientStage() (same predicate AgingChip uses, so the
// queue identities here are guaranteed to match row-level chips when we wire
// them back in). Middle is today + week-out scheduling. Bottom is revenue,
// gated behind can('view_financials') so only super-admins see financials.

// ---- Queue widget definitions ---------------------------------------------
// Each entry: stage key, headline label, tagline shown under headline, and the
// row-CTA label that shows on the right when a row is hovered. Colors describe
// the accent the widget uses for borders/highlights so urgent things read hot.
// Order = render order in the 3-col grid (slots 1-2-3 top row, 4-5-6 bottom).
// 2026-08-07 REWORK (Jaylen: "trim the fat"): every queue now fills AND
// drains on a real event — no queue depends on a manual stamp. "Needs
// Callback" (drained only by the dead Mark-First-Contact button) became
// Needs Follow-up (drains when a walkthrough is scheduled / estimate
// exists). "Ready to Send" + "Awaiting Decision" (needed quote_given_at
// stamping nobody did) merged into Estimates Awaiting Booking (estimate
// exists → drains on booked). The freed slot = Pending Recaps (dev note
// 392de4e1) — today's dispatched jobs missing a lead recap.
const QUEUE_DEFS = [
    {
        stage: STAGE_NEEDS_FOLLOWUP,
        title: 'Needs Follow-up',
        subtitle: 'We talked — nothing scheduled yet',
        accent: 'red',
    },
    {
        stage: STAGE_AWAITING_WT,
        title: 'Walkthroughs Scheduled',
        subtitle: 'Upcoming on-site appointments',
        accent: 'slate',
    },
    {
        // Slot 3 = Pending Recaps (Jaylen 2026-08-07: swapped with the
        // review queue — recaps are a same-day operational check, so they
        // belong in the top row's at-a-glance real estate).
        stage: '_pending_recaps',
        recaps: true,
        title: 'Pending Recaps',
        subtitle: "Today's jobs — recap expected from the lead",
        accent: 'emerald',
    },
    {
        stage: STAGE_AWAITING_BOOKING,
        title: 'Estimates Awaiting Booking',
        subtitle: 'Estimate exists — close it or follow up',
        accent: 'blue',
    },
    {
        stage: STAGE_NEAR_MOVE,
        title: 'Move Date Approaching',
        subtitle: 'Within 7 days — confirm crew, truck, payment',
        accent: 'red',
    },
    {
        stage: STAGE_WT_REVIEW,
        title: 'Walkthroughs Awaiting Review',
        subtitle: 'Walkthrough complete — needs estimate built',
        accent: 'amber',
    },
];

// Accent → color classes. We keep palette small so the page reads cohesively.
// `chip` is the hex text color for the row's right-side <StatusPill> (tint is derived from it).
const ACCENT_STYLES = {
    red:     { border: 'border-red-200',     headerBg: 'bg-red-50',     headerText: 'text-red-700',     countBg: 'bg-red-600',     chip: '#b91c1c' },
    amber:   { border: 'border-amber-200',   headerBg: 'bg-amber-50',   headerText: 'text-amber-800',   countBg: 'bg-amber-500',   chip: '#92400e' },
    blue:    { border: 'border-blue-200',    headerBg: 'bg-blue-50',    headerText: 'text-blue-700',    countBg: 'bg-blue-600',    chip: '#1d4ed8' },
    slate:   { border: 'border-slate-200',   headerBg: 'bg-slate-50',   headerText: 'text-slate-700',   countBg: 'bg-slate-700',   chip: '#334155' },
    emerald: { border: 'border-emerald-200', headerBg: 'bg-emerald-50', headerText: 'text-emerald-800', countBg: 'bg-emerald-600', chip: '#065f46' },
};

// Format a "how long has this been sitting" string from a since timestamp.
function formatSince(since) {
    if (!since) return '';
    const ms = Date.now() - new Date(since).getTime();
    const hours = ms / (1000 * 60 * 60);
    if (hours < 1) return '<1h';
    if (hours < 24) return `${Math.floor(hours)}h`;
    return `${Math.floor(hours / 24)}d`;
}

// Format "X days until move" / "today" for the STAGE_NEAR_MOVE rows.
function formatDaysUntil(moveDate) {
    if (!moveDate) return '';
    const ms = new Date(moveDate).getTime() - Date.now();
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'TODAY';
    if (days === 1) return 'TOMORROW';
    return `IN ${days}D`;
}

// Format "X days until walkthrough" for STAGE_AWAITING_WT rows.
function formatDaysUntilWalkthrough(scheduledAt) {
    if (!scheduledAt) return '';
    const ms = new Date(scheduledAt).getTime() - Date.now();
    const days = ms / (1000 * 60 * 60 * 24);
    if (days < 0) return `OVERDUE ${Math.abs(Math.floor(days))}D`;
    if (days < 1) return 'TODAY';
    if (days < 2) return 'TOMORROW';
    return `IN ${Math.floor(days)}D`;
}

function Dashboard() {
    const { crew, clients, jobs, estimates, moves, confirmMoveDates } = useStore();
    const { setCurrentPage, navigateToDispatch, navigateToClient, navigateToNewClient } = useNav();
    const { can, user, displayName } = useAuth();
    const { show: showToast } = useToast();
    const canViewFinancials = can('view_financials');
    const today = format(new Date(), 'yyyy-MM-dd');

    // Week boundaries (Monday-based)
    const todayDate = new Date();
    const weekStart = new Date(todayDate);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
    const thisWeekStart = format(weekStart, 'yyyy-MM-dd');
    const thisWeekEnd = format(addDays(weekStart, 6), 'yyyy-MM-dd');

    // Month boundaries
    const monthStart = format(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1), 'yyyy-MM-dd');
    const monthEnd = format(new Date(todayDate.getFullYear(), todayDate.getMonth() + 1, 0), 'yyyy-MM-dd');

    const todayJobs = jobs.filter(j => j.move_date === today);
    const weekJobs = jobs.filter(j => j.move_date >= thisWeekStart && j.move_date <= thisWeekEnd);
    const monthJobs = jobs.filter(j => j.move_date >= monthStart && j.move_date <= monthEnd);

    // ---- Build the six queues from leadPipeline predicates ----------------
    // We compute stage once per client and bucket. Multiple stages cannot
    // coexist (getClientStage returns at most one), so each client lands in at
    // most one widget. Inside each bucket, sort by "most overdue" — for time-
    // based stages that's oldest `since` first; for move-date that's soonest
    // move first.
    const queues = useMemo(() => {
        const buckets = {
            [STAGE_NEAR_MOVE]: [],
            [STAGE_NEEDS_FOLLOWUP]: [],
            [STAGE_WT_REVIEW]: [],
            [STAGE_AWAITING_BOOKING]: [],
            [STAGE_AWAITING_WT]: [],
        };

        for (const client of clients) {
            // `moves` → MOVE SOON reads the client's current move, not the
            // client.move_date mirror (moves rule 1, 2026-09-18).
            const stageInfo = getClientStage(client, estimates, jobs, { moves });
            if (!stageInfo) continue;
            if (!(stageInfo.stage in buckets)) continue;
            buckets[stageInfo.stage].push({ client, ...stageInfo });
        }

        // Sort each bucket: most urgent first.
        // - NEAR_MOVE: ascending by move date (today first)
        // - AWAITING_WT: ascending by walkthrough_date (today first)
        // - others: ascending by `since` (oldest first = most stale)
        buckets[STAGE_NEAR_MOVE].sort((a, b) =>
            (a.moveDate || a.client.move_date || '').localeCompare(b.moveDate || b.client.move_date || '')
        );
        buckets[STAGE_AWAITING_WT].sort((a, b) =>
            (a.client.walkthrough_date || '').localeCompare(b.client.walkthrough_date || '')
        );
        for (const stage of [STAGE_NEEDS_FOLLOWUP, STAGE_WT_REVIEW, STAGE_AWAITING_BOOKING]) {
            buckets[stage].sort((a, b) => (a.since || '').localeCompare(b.since || ''));
        }

        return buckets;
    }, [clients, estimates, jobs, moves]);

    // ---- Dates to confirm (moves rule 6, 2026-09-18) -----------------------
    // Booked / in-progress moves inside 7 days with any date still
    // tentative. Fills when a date is left tentative at booking (rule 4 —
    // booking never refuses on it), drains when someone confirms here, on
    // the estimate, or in the client file. Replaces the memory-and-calendar
    // habit of "did we ever call the Lees back about Sunday?".
    const toConfirm = useMemo(() => datesToConfirm(moves || [], { days: 7 }), [moves]);
    const [confirming, setConfirming] = useState(null); // move id in flight
    const byName = displayName || null;
    const handleConfirmMove = async (move) => {
        setConfirming(move.id);
        try {
            await confirmMoveDates(move.id, tentativeKeys(move), byName);
            const client = clients.find(c => c.id === move.client_id);
            showToast(`Dates confirmed for ${client?.name || 'the client'}`);
        } catch (e) {
            showToast('Error: ' + (e?.message || e), 'error');
        } finally { setConfirming(null); }
    };

    // ---- Pending Recaps (dev note 392de4e1, 2026-08-07) --------------------
    // Today's (and the last week's) real jobs that went out but have no
    // lead recap yet. Fills automatically when a job is dispatched/run,
    // drains automatically when the lead (or office) submits the recap.
    const pendingRecaps = useMemo(() => {
        const weekAgo = format(addDays(new Date(), -7), 'yyyy-MM-dd');
        return jobs
            .filter(j => j.type !== 'WALKTHROUGH'
                && ['dispatched', 'in_progress', 'completed'].includes(j.status)
                && !j.recap_submitted_at
                && j.move_date && j.move_date <= today && j.move_date >= weekAgo)
            .sort((a, b) => (b.move_date || '').localeCompare(a.move_date || '') || (a.time || '').localeCompare(b.time || ''));
    }, [jobs, today]);

    // ---- Revenue (super-admin only) ---------------------------------------
    const revenueStats = useMemo(() => {
        if (!canViewFinancials) return null;
        const getJobRevenue = (job) => {
            if (job.total_charged) return parseFloat(job.total_charged) || 0;
            const est = estimates.find(e => e.id === job.estimate_id);
            return parseFloat(est?.total_estimate) || 0;
        };
        const weekRevenue = weekJobs.reduce((sum, j) => sum + getJobRevenue(j), 0);
        const monthRevenue = monthJobs.reduce((sum, j) => sum + getJobRevenue(j), 0);
        const unpaidJobs = jobs.filter(j => j.invoice_number && !j.invoice_paid);
        const unpaidTotal = unpaidJobs.reduce((sum, j) => sum + getJobRevenue(j), 0);
        const paidThisMonth = monthJobs.filter(j => j.invoice_paid).reduce((sum, j) => sum + getJobRevenue(j), 0);
        return { weekRevenue, monthRevenue, unpaidJobs, unpaidTotal, paidThisMonth };
    }, [canViewFinancials, weekJobs, monthJobs, jobs, estimates]);

    const fmtMoney = (n) => '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    // ---- Render a single queue widget -------------------------------------
    // Pulled inline rather than into its own file because the rendering is
    // tightly coupled to the bucketing above (uses the right "right-side" hint
    // per stage). If this grows, lift it.
    const renderQueueRow = (entry, stage) => {
        const { client } = entry;
        // The move date for the row: the current move's (entry.moveDate,
        // from getClientStage with `moves`) else the mirror column.
        const moveDate = entry.moveDate || client.move_date;
        const moveTentative = entry.moveTentative ?? !!client.date_tentative;
        let rightHint = '';
        if (stage === STAGE_NEAR_MOVE) rightHint = formatDaysUntil(moveDate);
        else if (stage === STAGE_AWAITING_WT) rightHint = formatDaysUntilWalkthrough(client.walkthrough_date);
        else rightHint = formatSince(entry.since);

        const accent = QUEUE_DEFS.find(q => q.stage === stage)?.accent || 'slate';
        const styles = ACCENT_STYLES[accent];

        return (
            <div
                key={client.id}
                onClick={() => navigateToClient(client.id)}
                className={listRowCompactCls}
            >
                <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm truncate">{client.name || 'Unnamed lead'}</div>
                    {moveDate && (
                        <div className="text-xs text-slate-500 truncate">
                            Moving <span className={moveTentative ? 'text-amber-700 font-semibold' : ''} title={moveTentative ? 'Date still tentative' : undefined}>{fmtMoveDate(moveDate, { tentative: moveTentative })}</span>
                            {client.from_address ? ` · ${client.from_address.split(',')[0]}` : ''}
                        </div>
                    )}
                </div>
                <StatusPill label={rightHint} color={styles.chip} className="flex-shrink-0" />
            </div>
        );
    };

    // Pending Recaps rows are JOBS, not clients — separate renderer.
    const renderRecapRow = (job) => {
        const client = clients.find(c => c.id === job.client_id);
        const leader = crew.find(c => c.id === job.leader_id);
        const isToday = job.move_date === today;
        return (
            <div
                key={job.id}
                onClick={() => setCurrentPage('moves')}
                className={listRowCompactCls}
            >
                <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm truncate">{client?.name || 'Unknown'}</div>
                    <div className="text-xs text-slate-500 truncate">
                        {isToday ? 'Today' : format(parseISO(job.move_date), 'MMM d')}{leader ? ` · Lead: ${leader.name}` : ''} · {(job.status || '').replace(/_/g, ' ')}
                    </div>
                </div>
                <StatusPill label={job.status === 'completed' ? 'NO RECAP' : 'DUE TODAY'} color={job.status === 'completed' ? '#b91c1c' : '#065f46'} className="flex-shrink-0" />
            </div>
        );
    };

    // "+N more" expand/collapse per queue (Jaylen 2026-08-10 — the old
    // "+N more" text was a dead div). Expanded widgets show every row;
    // the container's max-h + overflow-y keeps them scrollable.
    const [expandedQueues, setExpandedQueues] = useState(() => new Set());
    const toggleExpanded = (key) => setExpandedQueues(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    // "Margie Lee · 021 — unload ~Sun Sep 20, load ~Sat Sep 19"
    const tentativeText = (move) => dateKeysOf(move).filter(k => k.tentative)
        .map(k => `${k.label.toLowerCase()} ${k.windowEnd ? fmtMoveDate(k.date, { tentative: true, windowEnd: k.windowEnd }) : `~${format(parseISO(k.date), 'EEE MMM d')}`}`)
        .join(', ');

    // Dates to confirm — same card recipe as the queue widgets (header
    // strip + count bubble + ruled compact rows), amber because it's a
    // "call the client" list, not a fire.
    const renderDatesToConfirm = () => {
        const styles = ACCENT_STYLES.amber;
        return (
            <div key="_dates_to_confirm" className={`bg-white rounded-xl border ${styles.border} overflow-hidden flex flex-col`}>
                <div className={`px-4 py-3 ${styles.headerBg} border-b ${styles.border} flex items-center justify-between`}>
                    <div className="min-w-0">
                        <h3 className={`font-lemonmilk text-sm font-bold ${styles.headerText} truncate`}>Dates to confirm</h3>
                        <p className="text-[11px] text-slate-600 mt-0.5 truncate">Booked, inside 7 days, a date still tentative</p>
                    </div>
                    <span className={`text-white text-sm font-bold ${styles.countBg} rounded-full min-w-[28px] h-7 px-2 flex items-center justify-center flex-shrink-0`}>
                        {toConfirm.length}
                    </span>
                </div>
                <div className={`${listPanelCls} rounded-none border-0 flex-1 min-h-[80px] max-h-[280px] overflow-y-auto`}>
                    {toConfirm.length === 0 ? (
                        <div className="text-center py-6 text-xs text-slate-400">Nothing to confirm this week.</div>
                    ) : toConfirm.map(({ move }) => {
                        const client = clients.find(c => c.id === move.client_id);
                        const num = (move.move_number || '').replace(/^BBM-\d{4}-/, '');
                        return (
                            <div key={move.id} onClick={() => navigateToClient(move.client_id)} className={listRowCompactCls}>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-slate-900 text-sm truncate">{client?.name || 'Unknown client'}{num ? <span className="text-slate-400 font-medium"> · {num}</span> : ''}</div>
                                    <div className="text-xs text-amber-700 font-semibold truncate">{tentativeText(move)}</div>
                                </div>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    loading={confirming === move.id}
                                    onClick={(e) => { e.stopPropagation(); handleConfirmMove(move); }}
                                >
                                    Confirm
                                </Button>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderQueueWidget = (def) => {
        const entries = def.recaps ? pendingRecaps : (queues[def.stage] || []);
        const styles = ACCENT_STYLES[def.accent];
        const isExpanded = expandedQueues.has(def.stage);
        return (
            <div key={def.stage} className={`bg-white rounded-xl border ${styles.border} overflow-hidden flex flex-col`}>
                <div className={`px-4 py-3 ${styles.headerBg} border-b ${styles.border} flex items-center justify-between`}>
                    <div className="min-w-0">
                        <h3 className={`font-lemonmilk text-sm font-bold ${styles.headerText} truncate`}>{def.title}</h3>
                        <p className="text-[11px] text-slate-600 mt-0.5 truncate">{def.subtitle}</p>
                    </div>
                    <span className={`text-white text-sm font-bold ${styles.countBg} rounded-full min-w-[28px] h-7 px-2 flex items-center justify-center flex-shrink-0`}>
                        {entries.length}
                    </span>
                </div>
                {/* Ruled list of compact queue rows (lists.md §2). */}
                <div className={`${listPanelCls} rounded-none border-0 flex-1 min-h-[80px] max-h-[280px] overflow-y-auto`}>
                    {entries.length === 0 ? (
                        <div className="text-center py-6 text-xs text-slate-400">
                            Nothing here right now
                        </div>
                    ) : (
                        (isExpanded ? entries : entries.slice(0, 8)).map(entry => def.recaps ? renderRecapRow(entry) : renderQueueRow(entry, def.stage))
                    )}
                    {entries.length > 8 && (
                        <Button
                            variant="tertiary"
                            size="sm"
                            full
                            className="mt-1"
                            onClick={() => toggleExpanded(def.stage)}
                        >
                            {isExpanded ? 'Show less' : `+${entries.length - 8} more — show all`}
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={pageWrapCls}>
            {/* Header — quick actions live in the header's actions slot */}
            <PageHeader
                title="Dashboard"
                summary={format(new Date(), 'EEEE, MMMM d, yyyy')}
                help={<>
                    <p>Today at a glance: the 14-day capacity strip, the lead and operational queues that need a next step, today's schedule and the week ahead.</p>
                    <p className="mt-2">Click a queue row to open that client or job. The quick-action buttons at the right jump to the intake form, the dispatch board, Active Moves and Records.</p>
                    <p className="mt-2"><span className="font-semibold">Dates to confirm</span> lists booked moves inside 7 days with a date still tentative (~). Confirm records who confirmed and when — the date stays editable afterwards.</p>
                </>}
                actions={[
                    // Opens the intake form directly via the nav flag — the
                    // old setTimeout+fake-click raced the page render and
                    // often left the user on the list needing a 2nd click.
                    { label: 'New Client', action: () => navigateToNewClient(), variant: 'primary' },
                    { label: 'Dispatch Board', action: () => navigateToDispatch(today), variant: 'secondary' },
                    { label: 'Active Moves', action: () => setCurrentPage('moves'), variant: 'secondary' },
                    { label: 'Records', action: () => setCurrentPage('records'), variant: 'secondary' },
                ].map(btn => (
                    <Button key={btn.label} variant={btn.variant} onClick={btn.action}>{btn.label}</Button>
                ))}
            />

            {/* ===== 14-day capacity glance (shared math with /#capacity) ===== */}
            <CapacityStrip />

            {/* ===== Lead/Operational Queues (top of page) ===== */}
            <div>
                <h2 className="font-lemonmilk text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Active Queues</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {/* Dates to confirm sits right after Move Date Approaching — same week, same phone call. */}
                    {QUEUE_DEFS.flatMap(def => def.stage === STAGE_NEAR_MOVE ? [renderQueueWidget(def), renderDatesToConfirm()] : [renderQueueWidget(def)])}
                </div>
            </div>

            {/* ===== Operational Stats + Today's Schedule ===== */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Today's Schedule */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold font-lemonmilk text-slate-900">Today's Schedule</h2>
                        {todayJobs.length > 0 && <Button variant="tertiary" size="sm" onClick={() => navigateToDispatch(today)}>View Dispatch →</Button>}
                    </div>
                    {todayJobs.length === 0 ? <p className="text-slate-500 text-sm py-4 text-center">No jobs today</p> : (
                        <div className="space-y-2">
                            {todayJobs.sort((a, b) => (a.time || '').localeCompare(b.time || '')).map(job => {
                                const client = clients.find(c => c.id === job.client_id);
                                const leader = crew.find(c => c.id === job.leader_id);
                                const jobType = job.job_label || (job.type || '').replace(/_/g, ' ');
                                return (
                                    <div key={job.id} onClick={() => navigateToClient(job.client_id)} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="w-1 h-12 rounded-full flex-shrink-0" style={{ backgroundColor: leader?.display_color || '#cbd5e1' }}></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-slate-900 text-sm truncate">{client?.name || 'TBD'}</div>
                                            <div className="text-xs text-slate-500 truncate">{job.time || 'TBD'} · {jobType} · {(job.crew_ids || []).length} crew{leader ? ` · ${leader.name}` : ''}</div>
                                        </div>
                                        <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${job.status === 'dispatched' ? 'bg-green-100 text-green-700' : job.status === 'assigned' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>{job.status}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Week Overview */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h2 className="text-lg font-bold font-lemonmilk text-slate-900 mb-4">Week Overview</h2>
                    <div className="grid grid-cols-7 gap-2">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => {
                            const dateStr = format(addDays(weekStart, i), 'yyyy-MM-dd');
                            const dayJobs = jobs.filter(j => j.move_date === dateStr);
                            const count = dayJobs.length;
                            const isToday2 = dateStr === today;
                            const crewCount = [...new Set(dayJobs.flatMap(j => j.crew_ids || []))].length;
                            return (
                                <div key={d} onClick={() => navigateToDispatch(dateStr)} className={`text-center p-2 rounded-lg cursor-pointer transition-all ${isToday2 ? 'bg-red-50 border-2 border-red-300 ring-1 ring-red-200' : 'bg-slate-50 border border-slate-200 hover:bg-slate-100'}`}>
                                    <div className={`text-[10px] font-semibold uppercase ${isToday2 ? 'text-red-600' : 'text-slate-500'}`}>{d}</div>
                                    <div className={`text-xl font-bold mt-0.5 ${isToday2 ? 'text-red-600' : count > 0 ? 'text-slate-900' : 'text-slate-300'}`}>{count}</div>
                                    <div className="text-[9px] text-slate-400 mt-0.5">{count > 0 ? `${crewCount} crew` : 'free'}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ===== Revenue (super-admin only — bottom of dashboard) ===== */}
            {canViewFinancials && revenueStats && (
                <div className="pt-2">
                    <div className="flex items-center gap-2 mb-3">
                        <h2 className="font-lemonmilk text-xs font-bold uppercase tracking-wider text-slate-500">Revenue</h2>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-500 uppercase tracking-wide">Owners only</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            { label: 'Week Revenue', value: fmtMoney(revenueStats.weekRevenue), color: 'text-emerald-600' },
                            { label: format(new Date(), 'MMMM') + ' Revenue', value: fmtMoney(revenueStats.monthRevenue), color: 'text-emerald-600' },
                            { label: 'Collected', value: fmtMoney(revenueStats.paidThisMonth), color: 'text-blue-600' },
                            { label: 'Unpaid Invoices', value: revenueStats.unpaidJobs.length > 0 ? `${revenueStats.unpaidJobs.length} (${fmtMoney(revenueStats.unpaidTotal)})` : 'None', color: revenueStats.unpaidJobs.length > 0 ? 'text-amber-600' : 'text-slate-400' },
                        ].map(card => (
                            <div key={card.label} className="bg-white rounded-xl border border-slate-200 p-5">
                                <div className="text-slate-500 text-xs font-semibold uppercase tracking-wider">{card.label}</div>
                                <div className={`text-xl font-bold mt-1 ${card.color}`}>{card.value}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Dashboard;
